import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, LogOut, UtensilsCrossed, ShoppingBag, Copy } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";
import { B2B_CHANNEL_TYPE_LABELS, type B2bChannelType } from "@/lib/b2b-channel-types";
import {
  formatSelectionLine,
  qtyMapFromSelections,
  selectionRows,
} from "@/lib/institutional-order-utils";
import { toast } from "sonner";
import { safeReturnPath } from "@/lib/market-institutional-auth";
import { MarketFunnelTracker } from "@/components/market/MarketFunnelTracker";

export const Route = createFileRoute("/institucional/$slug")({
  component: InstitucionalPortalPage,
  head: () => ({
    meta: [
      { title: "Portal institucional — Structura Market" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const SESSION_KEY = (slug: string) => `sm.institutional.${slug}`;

interface PortalChannel {
  id: string;
  name: string;
  channelType: B2bChannelType;
  description: string | null;
}

interface PortalData {
  tenant: { name: string; slug: string; logoUrl: string | null };
  config: {
    primaryColor?: string;
    heroTitle?: string;
    heroSubtitle?: string;
    orderDeadlineHour?: number;
    paymentMethods?: string[];
    mpEnabled?: boolean;
    bankTransfer?: {
      holder: string | null;
      cbu: string | null;
      alias: string | null;
      bank: string | null;
      notes: string | null;
    };
  } | null;
  channels: PortalChannel[];
  refChannel?: { id: string; name: string; channelType: string } | null;
}

interface InstUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  institutionName: string | null;
  channelId: string | null;
  channelName: string | null;
  channelType: B2bChannelType | null;
  creditLimit?: number | null;
  creditUsed?: number;
}

interface MenuRow {
  id: string;
  name: string;
  dateFrom: string;
  dateTo: string;
}

interface MenuItemRow {
  id: string;
  productName: string;
  sectionLabel: string | null;
  availableDate: string;
  price: number | null;
  imageUrl?: string | null;
}

interface MenuDetailRow extends MenuRow {
  items: MenuItemRow[];
}

interface CatalogItemRow {
  id: string;
  name: string;
  tipoVenta: string;
  b2bPrecioUnitario: number | null;
  b2bPrecioPorKg: number | null;
  discountPct: number;
  imagenUrl?: string | null;
}

interface SessionPayload {
  token?: string;
  user: InstUser;
  channel: { id: string; name: string; channelType: B2bChannelType } | null;
  menus: MenuRow[];
  menusDetailed: MenuDetailRow[];
  catalogItems: CatalogItemRow[];
}

interface InstitutionalOrderRow {
  id: string;
  menuName: string | null;
  total: number;
  status: string;
  createdAt: string;
  selections?: Record<string, unknown>;
}

function InstitucionalPortalPage() {
  const { slug } = Route.useParams();
  const [portal, setPortal] = useState<PortalData | null>(null);
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<"login" | "register">("login");
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [institutionName, setInstitutionName] = useState("");
  const [channelId, setChannelId] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [qtyByKey, setQtyByKey] = useState<Record<string, number>>({});
  const [orderNotes, setOrderNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cuenta_corriente");
  const [orders, setOrders] = useState<InstitutionalOrderRow[]>([]);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);

  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY(slug));
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as SessionPayload;
        setSession(parsed);
      } catch { /* ignore */ }
    }
  }, [slug]);

  useEffect(() => {
    void fetch(`/api/institucional/${slug}${typeof window !== "undefined" && window.location.search ? window.location.search : ""}`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status === 403 ? "Portal no disponible en este plan" : "No encontrado");
        return r.json() as Promise<PortalData>;
      })
      .then((data) => {
        setPortal(data);
        if (data.refChannel?.id) {
          setChannelId(data.refChannel.id);
          setInviteCode("");
        } else if (typeof window !== "undefined") {
          const ref = new URLSearchParams(window.location.search).get("ref");
          if (ref) setInviteCode(ref);
        }
      })
      .catch((e) => {
        toast.error(e instanceof Error ? e.message : "Error");
        setPortal(null);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const post = async (body: Record<string, unknown>, authToken?: string | null) => {
    const token = authToken ?? session?.token;
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const r = await fetch(`/api/institucional/${slug}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error((data as { error?: string }).error ?? "Error");
    return data;
  };

  const loadOrders = async () => {
    if (!session?.token && !session?.user.id) return;
    try {
      const res = await post({ action: "list_orders" }) as { orders?: InstitutionalOrderRow[] };
      setOrders(res.orders ?? []);
    } catch {
      setOrders([]);
    }
  };

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await post({ action: "login", email, password }) as SessionPayload;
      setSession(res);
      sessionStorage.setItem(SESSION_KEY(slug), JSON.stringify(res));
      toast.success(`Bienvenido/a, ${res.user.firstName}`);
      // U3: volver al storefront si vino con ?return=
      const ret = safeReturnPath(new URLSearchParams(window.location.search).get("return"));
      if (ret) {
        window.location.assign(ret);
        return;
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const onRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await post({
        action: "register",
        firstName,
        lastName,
        email,
        username,
        password,
        institutionName,
        channelId: channelId && channelId !== "_none" ? channelId : undefined,
        inviteCode: inviteCode.trim() || undefined,
      }) as { message?: string };
      toast.success(res.message ?? "Solicitud enviada");
      setMode("login");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY(slug));
    setSession(null);
    setQtyByKey({});
    setOrderNotes("");
    setOrders([]);
  };

  useEffect(() => {
    if (!session?.user.id) return;
    void loadOrders();
  }, [session?.user.id, session?.token]);

  const color = portal?.config?.primaryColor ?? "#16a34a";
  const pesos = (n: number | null) =>
    n != null ? n.toLocaleString("es-AR", { style: "currency", currency: "ARS" }) : "—";
  const isoDate = (v: string) => new Date(v).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });

  const cartTotal = useMemo(() => {
    if (!session) return 0;
    let total = 0;
    if (session.channel?.channelType === "menus" || session.user.channelType === "menus") {
      for (const menu of session.menusDetailed) {
        for (const item of menu.items) {
          const key = `m:${menu.id}:${item.id}`;
          const qty = qtyByKey[key] ?? 0;
          if (qty > 0) total += qty * Number(item.price ?? 0);
        }
      }
    } else {
      for (const item of session.catalogItems) {
        const qty = qtyByKey[`c:${item.id}`] ?? 0;
        if (qty <= 0) continue;
        const unit = item.tipoVenta === "peso" ? Number(item.b2bPrecioPorKg ?? 0) : Number(item.b2bPrecioUnitario ?? 0);
        total += qty * unit;
      }
    }
    return total;
  }, [session, qtyByKey]);

  const b2bPaymentOptions = useMemo(() => {
    const methods = portal?.config?.paymentMethods ?? [];
    const opts: Array<{ id: string; label: string }> = [{ id: "cuenta_corriente", label: "Cuenta corriente / factura" }];
    if (methods.includes("transferencia")) opts.push({ id: "transferencia", label: "Transferencia bancaria" });
    if (methods.includes("mercadopago") && portal?.config?.mpEnabled) opts.push({ id: "mercadopago", label: "Mercado Pago" });
    return opts;
  }, [portal?.config]);

  const changeQty = (key: string, next: number) => {
    const safe = Number.isFinite(next) ? Math.max(0, Math.floor(next)) : 0;
    setQtyByKey((prev) => ({ ...prev, [key]: safe }));
  };

  const cancelOrder = async (orderId: string) => {
    setBusy(true);
    try {
      await post({ action: "cancel_order", orderId });
      toast.success("Pedido cancelado");
      setOpenOrderId(null);
      await loadOrders();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cancelar");
    } finally {
      setBusy(false);
    }
  };

  const repeatOrder = (order: InstitutionalOrderRow) => {
    const map = qtyMapFromSelections(order.selections);
    if (!Object.keys(map).length) {
      toast.error("Este pedido no tiene ítems para repetir");
      return;
    }
    setQtyByKey(map);
    setOrderNotes("");
    setOpenOrderId(null);
    toast.success("Cantidades cargadas. Revisá y enviá el nuevo pedido.");
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const submitOrder = async () => {
    if (!session) return;
    const { user, channel, menusDetailed, catalogItems } = session;
    const selections: Record<string, unknown> = {};
    let total = 0;
    let menuId: string | null = null;

    if (channel?.channelType === "menus" || user.channelType === "menus") {
      for (const menu of menusDetailed) {
        for (const item of menu.items) {
          const key = `m:${menu.id}:${item.id}`;
          const qty = qtyByKey[key] ?? 0;
          if (qty <= 0) continue;
          const unitPrice = Number(item.price ?? 0);
          selections[key] = {
            menuId: menu.id,
            itemId: item.id,
            productName: item.productName,
            qty,
            unitPrice,
            total: qty * unitPrice,
            availableDate: item.availableDate,
          };
          total += qty * unitPrice;
          menuId = menu.id;
        }
      }
    } else {
      for (const item of catalogItems) {
        const key = `c:${item.id}`;
        const qty = qtyByKey[key] ?? 0;
        if (qty <= 0) continue;
        const unitPrice = Number(item.tipoVenta === "peso" ? (item.b2bPrecioPorKg ?? 0) : (item.b2bPrecioUnitario ?? 0));
        selections[key] = {
          itemId: item.id,
          productName: item.name,
          qty,
          unitPrice,
          total: qty * unitPrice,
          tipoVenta: item.tipoVenta,
        };
        total += qty * unitPrice;
      }
    }

    if (!Object.keys(selections).length) {
      toast.error("Agregá al menos un ítem con cantidad mayor a cero.");
      return;
    }

    setBusy(true);
    try {
      const res = await post({
        action: "create_order",
        menuId,
        selections,
        notes: orderNotes.trim() || undefined,
        paymentMethod,
      }) as {
        order?: { mpPaymentLink?: string | null };
        bankTransfer?: PortalData["config"] extends null ? never : NonNullable<PortalData["config"]>["bankTransfer"];
      };

      if (paymentMethod === "mercadopago" && res.order?.mpPaymentLink) {
        window.location.href = res.order.mpPaymentLink;
        return;
      }

      toast.success(
        paymentMethod === "transferencia"
          ? "Pedido registrado. Realizá la transferencia con los datos del proveedor."
          : "Pedido enviado correctamente",
      );
      setQtyByKey({});
      setOrderNotes("");
      await loadOrders();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al enviar pedido");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <PageLoader variant="screen" label="Cargando portal…" />;
  }

  if (!portal) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <div>
          <h1 className="text-xl font-bold">Portal no disponible</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Este negocio no tiene activado el módulo institucional (plan Business).
          </p>
          <Link to="/marketing" className="mt-4 inline-block text-sm text-primary hover:underline">
            Conocer Structura Market
          </Link>
        </div>
      </div>
    );
  }

  if (session) {
    const { user, channel, menus, catalogItems } = session;
    const isMenus = channel?.channelType === "menus" || user.channelType === "menus";

    const deadlineHour = portal.config?.orderDeadlineHour ?? 10;

    return (
      <div className="min-h-screen bg-background">
        <MarketFunnelTracker slug={slug} channel="b2b" />
        <header className="border-b border-border px-4 py-3 flex items-center justify-between" style={{ borderColor: `${color}33` }}>
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="h-5 w-5" style={{ color }} />
            <div>
              <p className="font-semibold">{portal.tenant.name}</p>
              <p className="text-xs text-muted-foreground">
                {channel?.name ?? user.channelName ?? "Portal institucional"}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout}>
            <LogOut className="h-4 w-4 mr-1" /> Salir
          </Button>
        </header>
        <p className="text-center text-xs text-muted-foreground bg-muted/40 py-1.5 px-4">
          Pedidos hasta las {deadlineHour}:00 hs (hora Argentina). Los totales se confirman al enviar.
        </p>
        <main className="mx-auto max-w-lg p-4 space-y-4">
          <p className="text-sm">
            Hola <strong>{user.firstName}</strong>
            {user.institutionName ? ` · ${user.institutionName}` : ""}
            {user.creditLimit != null
              ? ` · Crédito: $${Math.max(0, user.creditLimit - (user.creditUsed ?? 0)).toLocaleString("es-AR")} disp.`
              : ""}
          </p>

          {!channel && !user.channelId ? (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-muted-foreground">
              Tu cuenta está aprobada pero aún no tiene un canal B2B asignado. Contactá al proveedor para que te vinculen a tu convenio.
            </div>
          ) : isMenus ? (
            <>
              <h2 className="font-semibold flex items-center gap-2">
                <UtensilsCrossed className="h-4 w-4" /> Menús de tu canal
              </h2>
              {!session.menusDetailed.length ? (
                <p className="text-sm text-muted-foreground">Aún no hay menús publicados para tu canal.</p>
              ) : (
                <div className="space-y-4">
                  {session.menusDetailed.map((m) => (
                    <div key={m.id} className="rounded-xl border border-border p-4 space-y-3">
                      <div>
                        <p className="font-medium">{m.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{m.dateFrom} — {m.dateTo}</p>
                      </div>
                      {!m.items.length ? (
                        <p className="text-xs text-muted-foreground">Este menú no tiene ítems cargados.</p>
                      ) : (
                        <div className="space-y-2">
                          {m.items.map((item) => {
                            const k = `m:${m.id}:${item.id}`;
                            return (
                              <div key={item.id} className="rounded-lg border p-2 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  {item.imageUrl && (
                                    <img src={item.imageUrl} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                                  )}
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{item.productName}</p>
                                    <p className="text-[11px] text-muted-foreground">
                                      {item.sectionLabel ?? "Plato"} · {item.availableDate} · {pesos(item.price)}
                                    </p>
                                  </div>
                                </div>
                                <Input
                                  type="number"
                                  min={0}
                                  value={qtyByKey[k] ?? 0}
                                  onChange={(e) => changeQty(k, Number(e.target.value))}
                                  className="w-20 h-8"
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <h2 className="font-semibold flex items-center gap-2">
                <ShoppingBag className="h-4 w-4" /> Catálogo de tu canal
              </h2>
              {!catalogItems.length ? (
                <p className="text-sm text-muted-foreground">El catálogo de tu canal aún no tiene productos.</p>
              ) : (
                <ul className="space-y-2">
                  {catalogItems.map((item) => (
                    <li key={item.id} className="rounded-xl border border-border p-3 flex justify-between items-center gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {(item as CatalogItemRow & { catalogImagenUrl?: string; customImagenUrl?: string }).catalogImagenUrl && (
                          <img
                            src={(item as CatalogItemRow & { catalogImagenUrl?: string }).catalogImagenUrl!}
                            alt=""
                            className="h-10 w-10 rounded object-cover shrink-0"
                          />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.discountPct > 0 ? `${item.discountPct}% dto. sobre lista` : "Precio convenio"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold shrink-0" style={{ color }}>
                          {item.tipoVenta === "peso"
                            ? `${pesos(item.b2bPrecioPorKg)}/kg`
                            : pesos(item.b2bPrecioUnitario)}
                        </p>
                        <Input
                          type="number"
                          min={0}
                          value={qtyByKey[`c:${item.id}`] ?? 0}
                          onChange={(e) => changeQty(`c:${item.id}`, Number(e.target.value))}
                          className="w-20 h-8"
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {!!(channel || user.channelId) && (
            <div className="rounded-xl border border-border p-4 space-y-3">
              <div className="flex justify-between items-center text-sm font-semibold">
                <span>Total del pedido</span>
                <span style={{ color }}>{pesos(cartTotal)}</span>
              </div>
              {b2bPaymentOptions.length > 1 && (
                <div className="space-y-2">
                  <Label>Forma de pago</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {b2bPaymentOptions.map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Notas del pedido (opcional)</Label>
                <Input value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} placeholder="Observaciones para cocina/logística" />
              </div>
              <Button onClick={submitOrder} disabled={busy || cartTotal <= 0} className="w-full" style={{ backgroundColor: color }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar pedido institucional"}
              </Button>
            </div>
          )}
          <div className="rounded-xl border border-border p-4">
            <p className="font-semibold text-sm">Últimos pedidos</p>
            {!orders.length ? (
              <p className="text-xs text-muted-foreground mt-2">Todavía no registrás pedidos.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {orders.map((o) => (
                  <li key={o.id} className="text-xs rounded-md border border-border p-2">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-2 text-left"
                      onClick={() => setOpenOrderId((prev) => (prev === o.id ? null : o.id))}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{o.menuName ?? "Pedido catálogo"}</p>
                        <p className="text-muted-foreground">{isoDate(o.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{pesos(o.total)}</p>
                        <p className="text-muted-foreground">{o.status}</p>
                      </div>
                    </button>
                    {openOrderId === o.id && (
                      <div className="mt-2 border-t pt-2 space-y-2">
                        {selectionRows(o.selections).map((row, idx) => (
                          <p key={`${o.id}-${idx}`} className="text-muted-foreground">
                            {formatSelectionLine(row, (n) => pesos(n))}
                          </p>
                        ))}
                        <div className="flex flex-col gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              repeatOrder(o);
                            }}
                          >
                            <Copy className="h-3.5 w-3.5 mr-1" /> Repetir este pedido
                          </Button>
                          {o.status.toUpperCase() === "PENDIENTE" && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="w-full text-destructive border-destructive/40"
                              disabled={busy}
                              onClick={(e) => {
                                e.stopPropagation();
                                void cancelOrder(o.id);
                              }}
                            >
                              Cancelar pedido
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MarketFunnelTracker slug={slug} channel="b2b" />
      <header className="px-4 py-8 text-center" style={{ background: `linear-gradient(180deg, ${color}22, transparent)` }}>
        <h1 className="text-2xl font-bold">{portal.config?.heroTitle ?? portal.tenant.name}</h1>
        {portal.config?.heroSubtitle && (
          <p className="mt-2 text-sm text-muted-foreground">{portal.config.heroSubtitle}</p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">Acceso institucional B2B</p>
      </header>

      <main className="flex-1 mx-auto w-full max-w-md px-4 pb-8">
        <div className="flex gap-2 mb-6">
          <Button variant={mode === "login" ? "default" : "outline"} className="flex-1" onClick={() => setMode("login")}>
            Ingresar
          </Button>
          <Button variant={mode === "register" ? "default" : "outline"} className="flex-1" onClick={() => setMode("register")}>
            Solicitar acceso
          </Button>
        </div>

        {mode === "login" ? (
          <form onSubmit={onLogin} className="space-y-4 rounded-xl border border-border p-6">
            <div><Label>Email o usuario</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div><Label>Contraseña</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
            <Button type="submit" className="w-full" disabled={busy} style={{ backgroundColor: color }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
            </Button>
          </form>
        ) : (
          <form onSubmit={onRegister} className="space-y-3 rounded-xl border border-border p-6">
            <p className="text-xs text-muted-foreground pb-1">
              Si conocés tu canal o tenés un código de referencia del proveedor, completalo para que te asignen al convenio correcto.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nombre</Label><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required /></div>
              <div><Label>Apellido</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} required /></div>
            </div>
            <div><Label>Institución</Label><Input value={institutionName} onChange={(e) => setInstitutionName(e.target.value)} /></div>
            {portal.channels.length > 0 && (
              <div>
                <Label>Canal (si lo conocés)</Label>
                <Select value={channelId || "_none"} onValueChange={setChannelId}>
                  <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No sé / lo define el proveedor</SelectItem>
                    {portal.channels.map((ch) => (
                      <SelectItem key={ch.id} value={ch.id}>
                        {ch.name} · {B2B_CHANNEL_TYPE_LABELS[ch.channelType]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Código de referencia (opcional)</Label>
              <Input
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="ej: empresa-norte"
                className="font-mono"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Si tenés un código del proveedor, tiene prioridad sobre la selección de canal.
              </p>
            </div>
            <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div><Label>Usuario</Label><Input value={username} onChange={(e) => setUsername(e.target.value)} required /></div>
            <div><Label>Contraseña</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
            <Button type="submit" className="w-full" disabled={busy} style={{ backgroundColor: color }}>
              Enviar solicitud
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Un administrador debe aprobar tu cuenta antes de poder ingresar.
            </p>
          </form>
        )}
      </main>
    </div>
  );
}
